import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Available skeleton placeholder shapes. */
export type SkeletonShape = 'text' | 'circle' | 'rect' | 'list-item';

/**
 * Placeholder shape that renders while real data is loading. Uses `bg-surface-high`
 * plus a shimmer animation that respects `prefers-reduced-motion`. For whole-page
 * loading with an indeterminate top bar, use `LoadingBarComponent` instead.
 *
 * @example
 * <!-- Three lines of text placeholder -->
 * <app-skeleton shape="text" [count]="3" />
 *
 * @example
 * <!-- Avatar placeholder -->
 * <app-skeleton shape="circle" width="3rem" height="3rem" />
 *
 * @example
 * <!-- Card placeholder -->
 * <app-skeleton shape="rect" width="100%" height="10rem" rounded="lg" />
 *
 * @example
 * <!-- Composite list row (avatar + two lines) -->
 * <app-skeleton shape="list-item" [count]="5" />
 *
 * @feature shared
 */
@Component({
  selector: 'app-skeleton',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block', 'aria-hidden': 'true' },
  styles: [
    `
      :host {
        --skeleton-bg: var(--ion-color-surface-high);
        --skeleton-shine: var(--ion-color-surface-medium);
      }

      .skeleton-shape {
        background-color: var(--skeleton-bg);
        background-image: linear-gradient(
          90deg,
          var(--skeleton-bg) 0%,
          var(--skeleton-shine) 50%,
          var(--skeleton-bg) 100%
        );
        background-size: 200% 100%;
        animation: skeleton-shimmer 1.4s ease-in-out infinite;
      }

      @keyframes skeleton-shimmer {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .skeleton-shape {
          animation: none;
          background-image: none;
        }
      }
    `,
  ],
  template: `
    @switch (shape()) {
      @case ('list-item') {
        @for (_ of items(); track $index) {
          <div class="flex items-center gap-3" [class.mt-3]="$index > 0">
            <div class="skeleton-shape rounded-full shrink-0" style="width: 2.5rem; height: 2.5rem;"></div>
            <div class="flex-1 flex flex-col gap-2">
              <div class="skeleton-shape rounded" style="width: 60%; height: 0.875rem;"></div>
              <div class="skeleton-shape rounded" style="width: 40%; height: 0.75rem;"></div>
            </div>
          </div>
        }
      }
      @case ('circle') {
        @for (_ of items(); track $index) {
          <div
            class="skeleton-shape rounded-full"
            [class.mt-2]="$index > 0"
            [style.width]="width()"
            [style.height]="height()"
          ></div>
        }
      }
      @case ('rect') {
        @for (_ of items(); track $index) {
          <div
            class="skeleton-shape"
            [class]="radiusClass()"
            [class.mt-2]="$index > 0"
            [style.width]="width()"
            [style.height]="height()"
          ></div>
        }
      }
      @default {
        @for (_ of items(); track $index) {
          <div
            class="skeleton-shape rounded"
            [class.mt-2]="$index > 0"
            [style.width]="width()"
            [style.height]="height()"
          ></div>
        }
      }
    }

    <span class="sr-only">Loading…</span>
  `,
})
export class SkeletonComponent {
  /** Placeholder shape. @default 'text' */
  readonly shape = input<SkeletonShape>('text');

  /** Number of shapes to render. @default 1 */
  readonly count = input<number>(1);

  /** Width as any CSS length. @default '100%' */
  readonly width = input<string>('100%');

  /** Height as any CSS length. @default '1rem' */
  readonly height = input<string>('1rem');

  /** Border radius token. @default 'md' */
  readonly rounded = input<'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full'>('md');

  /** Iterable of length `count()` for the template `@for`. */
  readonly items = computed(() => Array.from({ length: Math.max(0, this.count()) }));

  /** Resolved Tailwind radius class for the rect shape. */
  readonly radiusClass = computed(() => {
    switch (this.rounded()) {
      case 'none':
        return 'rounded-none';
      case 'sm':
        return 'rounded-sm';
      case 'md':
        return 'rounded-md';
      case 'lg':
        return 'rounded-lg';
      case 'xl':
        return 'rounded-xl';
      case 'full':
        return 'rounded-full';
      default:
        return 'rounded-md';
    }
  });
}
